import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@weebee/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { Modal } from '@/components/ui/modal';
import { ErrorState } from '@/components/state/error-state';
import { cn } from '@/lib/utils';
import { queryKeys } from '@/lib/query-keys';
import { formatDateTime } from '@/lib/format';
import {
  getMaintenance,
  enableMaintenance,
  disableMaintenance,
  type MaintenanceMode,
} from '@/features/maintenance/api';
import { getAppCheck, setAppCheckMode, type AppCheckMode } from '@/features/app-check/api';
import { listAppVersions, setAppVersion, type AppVersion } from '@/features/app-version/api';

export const Route = createFileRoute('/_authenticated/controls/')({
  component: ControlsPage,
});

function ControlsPage() {
  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="flex flex-col gap-1.5" style={{ marginBottom: 20 }}>
        <span className="wb-eyebrow">Platform</span>
        <h1 className="wb-h1">System controls</h1>
        <p className="wb-sub">Fleet-wide switches. Changes take effect within seconds.</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <MaintenanceCard />
        <AppCheckCard />
        <AppVersionCard />
      </div>
    </div>
  );
}

/* ---------- Maintenance ---------- */

function maintenanceTone(mode: MaintenanceMode): BadgeTone {
  if (mode === 'blocked') return 'danger';
  if (mode === 'read_only') return 'warning';
  return 'success';
}

function MaintenanceCard() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: queryKeys.maintenance.status(), queryFn: getMaintenance });

  const [mode, setMode] = useState<'read_only' | 'blocked'>('read_only');
  const [message, setMessage] = useState('');
  const [retry, setRetry] = useState('60');
  const [confirm, setConfirm] = useState(false);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.status() });

  const enable = useMutation({
    mutationFn: () =>
      enableMaintenance({
        mode,
        message: message.trim() || undefined,
        retry_after_seconds: Number(retry) || undefined,
      }),
    onSuccess: async () => {
      setConfirm(false);
      await invalidate();
    },
  });

  const disable = useMutation({
    mutationFn: disableMaintenance,
    onSuccess: invalidate,
  });

  const current = query.data;

  return (
    <Card>
      <CardContent>
        <Header title="Maintenance mode" hint="Blocks or read-onlys the whole fleet." />
        {query.error ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '10px 0 16px' }}>
              <span className="wb-small">Current:</span>
              {current ? (
                <Badge tone={maintenanceTone(current.mode)}>{current.mode}</Badge>
              ) : (
                <span
                  className="wb-skeleton"
                  style={{ width: 60, height: 20, display: 'inline-block' }}
                />
              )}
              {current && current.mode !== 'off' ? (
                <Button
                  size="sm"
                  variant="secondary"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => disable.mutate()}
                  disabled={disable.isPending}
                >
                  {disable.isPending ? 'Disabling…' : 'Disable'}
                </Button>
              ) : null}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="wb-label">Mode</label>
                <Select
                  size="sm"
                  options={[
                    { value: 'read_only', label: 'Read only' },
                    { value: 'blocked', label: 'Blocked' },
                  ]}
                  value={mode}
                  onChange={(e) => setMode(e.target.value as 'read_only' | 'blocked')}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="wb-label">Message</label>
                <input
                  className="wb-field"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Shown to users (optional)"
                />
              </div>
              <div>
                <label className="wb-label">Retry after (s)</label>
                <input
                  className="wb-field"
                  type="number"
                  value={retry}
                  onChange={(e) => setRetry(e.target.value)}
                />
              </div>
            </div>

            <Button
              size="sm"
              variant="danger"
              style={{ marginTop: 14 }}
              onClick={() => setConfirm(true)}
            >
              Enable maintenance
            </Button>
            {enable.isError ? (
              <div className="wb-form-error">{getErrorMessage(enable.error)}</div>
            ) : null}
            {disable.isError ? (
              <div className="wb-form-error">{getErrorMessage(disable.error)}</div>
            ) : null}
          </>
        )}

        <Modal
          open={confirm}
          onClose={() => setConfirm(false)}
          title="Enable maintenance mode?"
          footer={
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirm(false)}
                disabled={enable.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => enable.mutate()}
                disabled={enable.isPending}
              >
                {enable.isPending ? 'Enabling…' : `Enable ${mode}`}
              </Button>
            </>
          }
        >
          <p className="wb-small">
            This puts the entire fleet into <strong>{mode}</strong> mode. Users will be blocked from
            {mode === 'blocked' ? ' all requests' : ' writes'} until you disable it.
          </p>
        </Modal>
      </CardContent>
    </Card>
  );
}

/* ---------- App Check ---------- */

const APP_CHECK_MODES: AppCheckMode[] = ['off', 'permissive', 'enforced'];

function AppCheckCard() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: queryKeys.appCheck.status(), queryFn: getAppCheck });
  const [pendingMode, setPendingMode] = useState<AppCheckMode | null>(null);

  const mutation = useMutation({
    mutationFn: (mode: AppCheckMode) => setAppCheckMode(mode),
    onSuccess: async () => {
      setPendingMode(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.appCheck.status() });
    },
  });

  const current = query.data?.mode;

  function selectMode(mode: AppCheckMode) {
    if (mode === current) return;
    if (mode === 'enforced') {
      setPendingMode(mode);
    } else {
      mutation.mutate(mode);
    }
  }

  return (
    <Card>
      <CardContent>
        <Header title="App Check" hint="Gates API access to genuine app builds." />
        {query.error ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : (
          <div style={{ marginTop: 12 }}>
            <div className="wb-segmented">
              {APP_CHECK_MODES.map((m) => (
                <button
                  key={m}
                  className={cn(current === m && 'active')}
                  disabled={mutation.isPending}
                  onClick={() => selectMode(m)}
                >
                  {m}
                </button>
              ))}
            </div>
            {mutation.isError ? (
              <div className="wb-form-error">{getErrorMessage(mutation.error)}</div>
            ) : null}
          </div>
        )}

        <Modal
          open={pendingMode === 'enforced'}
          onClose={() => setPendingMode(null)}
          title="Enforce App Check?"
          footer={
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPendingMode(null)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => mutation.mutate('enforced')}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Applying…' : 'Enforce'}
              </Button>
            </>
          }
        >
          <p className="wb-small">
            Enforcing rejects any request without a valid App Check token — sideloaded and debug
            builds will be locked out. Only Play/App Store builds pass.
          </p>
        </Modal>
      </CardContent>
    </Card>
  );
}

/* ---------- App versions ---------- */

function AppVersionCard() {
  const query = useQuery({ queryKey: queryKeys.appVersions.list(), queryFn: listAppVersions });

  return (
    <Card>
      <CardContent>
        <Header title="App versions" hint="Minimum supported version & force-update gate." />
        {query.error ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.isLoading ? (
          <span
            className="wb-skeleton"
            style={{ width: '100%', height: 80, display: 'block', marginTop: 12 }}
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2" style={{ marginTop: 12 }}>
            {(['android', 'ios'] as const).map((platform) => (
              <PlatformForm
                key={platform}
                platform={platform}
                config={query.data?.configs?.find((c) => c?.platform === platform) ?? null}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PlatformForm({ platform, config }: { platform: string; config: AppVersion | null }) {
  const queryClient = useQueryClient();
  const [minVersion, setMinVersion] = useState('');
  const [minBuild, setMinBuild] = useState('');
  const [forceUpdate, setForceUpdate] = useState(false);
  const [message, setMessage] = useState('');
  const [storeUrl, setStoreUrl] = useState('');

  useEffect(() => {
    setMinVersion(config?.minVersion ?? '');
    setMinBuild(config?.minBuild != null ? String(config.minBuild) : '');
    setForceUpdate(config?.forceUpdate ?? false);
    setMessage(config?.message ?? '');
    setStoreUrl(config?.storeUrl ?? '');
  }, [
    config?.id,
    config?.minVersion,
    config?.minBuild,
    config?.forceUpdate,
    config?.message,
    config?.storeUrl,
  ]);

  const mutation = useMutation({
    mutationFn: () =>
      setAppVersion(platform, {
        minVersion: minVersion.trim(),
        minBuild: Number(minBuild) || 0,
        forceUpdate,
        message: message.trim() || undefined,
        storeUrl: storeUrl.trim() || undefined,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.appVersions.list() }),
  });

  const valid = minVersion.trim().length > 0 && minBuild.trim().length > 0;

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Badge tone="info">{platform}</Badge>
        {config?.updatedAt ? (
          <span className="wb-tiny" style={{ marginLeft: 'auto' }}>
            updated {formatDateTime(config.updatedAt)}
          </span>
        ) : null}
      </div>
      <label className="wb-label">Min version</label>
      <input
        className="wb-field"
        value={minVersion}
        onChange={(e) => setMinVersion(e.target.value)}
        placeholder="1.0.0"
      />
      <label className="wb-label" style={{ marginTop: 10 }}>
        Min build
      </label>
      <input
        className="wb-field"
        type="number"
        value={minBuild}
        onChange={(e) => setMinBuild(e.target.value)}
        placeholder="42"
      />
      <label className="wb-label" style={{ marginTop: 10 }}>
        Update message
      </label>
      <input className="wb-field" value={message} onChange={(e) => setMessage(e.target.value)} />
      <label className="wb-label" style={{ marginTop: 10 }}>
        Store URL
      </label>
      <input className="wb-field" value={storeUrl} onChange={(e) => setStoreUrl(e.target.value)} />
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
        <input
          type="checkbox"
          checked={forceUpdate}
          onChange={(e) => setForceUpdate(e.target.checked)}
        />
        <span className="wb-small">Force update (block old versions)</span>
      </label>
      <Button
        size="sm"
        block
        style={{ marginTop: 14 }}
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending || !valid}
      >
        {mutation.isPending ? 'Saving…' : 'Save'}
      </Button>
      {mutation.isError ? (
        <div className="wb-form-error">{getErrorMessage(mutation.error)}</div>
      ) : null}
    </div>
  );
}

function Header({ title, hint }: { title: string; hint: string }) {
  return (
    <div>
      <div className="wb-h3">{title}</div>
      <p className="wb-small" style={{ color: 'var(--ink-2)' }}>
        {hint}
      </p>
    </div>
  );
}
